import React, { useState, useEffect, useRef } from 'react';
import { Building2, ChevronDown, Check } from 'lucide-react';
import { getBranches, getBranchMode } from '../../api/branches';

/**
 * BranchSelector - Dropdown component for selecting branch context
 * 
 * Used in multi-branch mode to allow users to switch between branches
 * or view "All Branches" for consolidated views.
 * 
 * Props:
 * - selectedBranchId: Currently selected branch ID (null = all branches)
 * - onBranchChange: Callback when branch selection changes
 * - showAllOption: Whether to show "All Branches" option (default: true)
 * - disabled: Disable the selector
 * - className: Additional CSS classes
 * - compact: Use compact sizing
 */
const BranchSelector = ({ 
  selectedBranchId = null, 
  onBranchChange, 
  showAllOption = true,
  disabled = false,
  className = '',
  compact = false
}) => {
  const [branches, setBranches] = useState([]);
  const [branchMode, setBranchMode] = useState(null);
  const [loading, setLoading] = useState(true);
  const [isOpen, setIsOpen] = useState(false);
  const dropdownRef = useRef(null);

  useEffect(() => {
    fetchData();
  }, []);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const fetchData = async () => {
    try {
      setLoading(true);
      const [branchesRes, modeRes] = await Promise.all([
        getBranches({ limit: 100, isActive: true }),
        getBranchMode()
      ]);
      setBranches(branchesRes.data || []);
      setBranchMode(modeRes.data);
    } catch (error) {
      console.error('Error fetching branches:', error);
    } finally {
      setLoading(false);
    }
  };

  // In single branch mode, don't show the selector
  if (branchMode?.singleBranchMode) {
    return null;
  }

  const selectedBranch = branches.find(b => b.id === selectedBranchId);
  const displayText = selectedBranch?.name || 'All Branches';

  const handleSelect = (branchId) => {
    onBranchChange?.(branchId);
    setIsOpen(false);
  };

  if (loading) {
    return (
      <div className={`inline-flex items-center gap-2 px-3 py-2 bg-gray-100 rounded-lg ${className}`}>
        <Building2 className="h-4 w-4 text-gray-400 animate-pulse" />
        <span className="text-sm text-gray-400">Loading...</span>
      </div>
    );
  }

  return (
    <div ref={dropdownRef} className={`relative ${className}`}>
      <button
        type="button"
        onClick={() => !disabled && setIsOpen(!isOpen)}
        disabled={disabled}
        className={`
          flex items-center gap-2 border rounded-lg transition-all
          ${compact ? 'px-2 py-1.5 text-sm' : 'px-3 py-2'}
          ${disabled ? 'bg-gray-100 text-gray-400 cursor-not-allowed' : 'bg-white hover:bg-gray-50 border-gray-300'}
          ${isOpen ? 'ring-2 ring-accent-gold border-accent-gold' : ''}
        `}
      >
        <Building2 className={`${compact ? 'h-4 w-4' : 'h-5 w-5'} text-accent-gold`} />
        <span className={`${compact ? 'max-w-24' : 'max-w-32'} truncate font-medium`}>
          {displayText}
        </span>
        <ChevronDown className={`h-4 w-4 text-gray-400 transition-transform ${isOpen ? 'rotate-180' : ''}`} />
      </button>

      {isOpen && (
        <div className="absolute z-50 mt-1 w-56 bg-white border border-gray-200 rounded-lg shadow-lg overflow-hidden">
          <div className="max-h-64 overflow-y-auto py-1">
            {/* All Branches Option */}
            {showAllOption && (
              <button
                onClick={() => handleSelect(null)}
                className={`
                  w-full flex items-center gap-3 px-3 py-2 text-left hover:bg-gray-50 transition-colors
                  ${selectedBranchId === null ? 'bg-accent-gold/10' : ''}
                `}
              >
                <div className="h-8 w-8 rounded-lg bg-gray-100 flex items-center justify-center">
                  <Building2 className="h-4 w-4 text-gray-500" />
                </div>
                <div className="flex-1 min-w-0">
                  <p className="font-medium text-gray-900">All Branches</p>
                  <p className="text-xs text-gray-500">Multi-branch sale</p>
                </div>
                {selectedBranchId === null && (
                  <Check className="h-4 w-4 text-accent-gold" />
                )}
              </button>
            )}

            {/* Divider */}
            {showAllOption && branches.length > 0 && (
              <div className="border-t border-gray-100 my-1" />
            )}

            {/* Branch Options */}
            {branches.map((branch) => (
              <button
                key={branch.id}
                onClick={() => handleSelect(branch.id)}
                className={`
                  w-full flex items-center gap-3 px-3 py-2 text-left hover:bg-gray-50 transition-colors
                  ${selectedBranchId === branch.id ? 'bg-accent-gold/10' : ''}
                `}
              >
                <div className="h-8 w-8 rounded-lg bg-accent-gold/10 flex items-center justify-center">
                  <Building2 className="h-4 w-4 text-accent-gold" />
                </div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-1">
                    <p className="font-medium text-gray-900 truncate">{branch.name}</p>
                    {branch.is_main && (
                      <span className="px-1.5 py-0.5 text-xs bg-accent-gold/20 text-accent-gold rounded">
                        Main
                      </span>
                    )}
                  </div>
                  <p className="text-xs text-gray-500">{branch.code}</p>
                </div>
                {selectedBranchId === branch.id && (
                  <Check className="h-4 w-4 text-accent-gold" />
                )}
              </button>
            ))}

            {branches.length === 0 && (
              <div className="px-3 py-4 text-center text-sm text-gray-500">
                No branches available
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default BranchSelector;
